import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    OnDestroy,
    OnInit,
    Optional,
    Output,
} from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { Actions, ofType } from '@ngrx/effects';
import { TranslateService } from '@ngx-translate/core';
import { IConfigColumnGridControl, IDataSource, isNotEmpty, IVisibilityStatus, JsonObject } from '@se/cp.common';
import { ButtonClick, CultureService, ModalDialogNotificationService, NotificationType } from '@se/platform';
import {
    Logger,
    SeConstraintApplierService,
    SeConstraintConfiguratorService,
    SeConstraintNotifierService,
    themeEstensaCustomPage,
} from '@se/se.platform.base';
import {
    AllCommunityModule,
    CellValueChangedEvent,
    Column,
    ColumnResizedEvent,
    FirstDataRenderedEvent,
    GetRowIdFunc,
    GridApi,
    GridReadyEvent,
    GridSizeChangedEvent,
    ICellRendererParams,
    IRowNode,
    RowDataUpdatedEvent,
    RowEditingStoppedEvent,
    RowSelectionOptions,
    StartEditingCellParams,
} from 'ag-grid-community';
import { isEqual, isNil } from 'lodash-es';
import { Subscription } from 'rxjs';
import { distinctUntilChanged, first, map, takeUntil } from 'rxjs/operators';

import { CpEntityList, CpGridControl, ICpEntity } from '../../cp-configuration/view-model';
import { CpDatasourceService } from '../../cp-datasource.service';
import { CpModelService } from '../../cp-model.service';
import { cpSave } from '../../store/cp.actions';
import { AbstractControlComponent } from '../abstract-control';

type JsonEntity = JsonObject & { Id: string; entity: ICpEntity };
@Component({
    selector: 'cp-grid',
    templateUrl: './grid.component.html',
    styleUrls: ['./grid.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class CpGridComponent extends AbstractControlComponent implements OnDestroy, OnInit {
    @Output() addRow = new EventEmitter<ICpEntity>();
    @Output() deleteRow = new EventEmitter<any>();

    control!: CpGridControl;
    model: JsonEntity[] | null = null;
    rowHeight: number = 50;
    rowSelection?: RowSelectionOptions;

    noRowsTemplate!: string;
    _enableButtonRemoveAllRows: boolean = false;

    // VALIDATION
    formGroup: FormGroup = new FormGroup({});
    // ------
    private gridApi?: GridApi;
    private subscriptions: Subscription[] = [];
    agGridModuls = [AllCommunityModule];
    agGridTheme = themeEstensaCustomPage;

    constructor(
        cdr: ChangeDetectorRef,
        constraintNotifierService: SeConstraintNotifierService,
        private actions$: Actions,
        private modelService: CpModelService,
        private datasourceService: CpDatasourceService,
        private cultureService: CultureService,
        private translate: TranslateService,
        private modalDialogNotificationService: ModalDialogNotificationService,
        private configuratorService: SeConstraintConfiguratorService,
        private constraintFilterService: SeConstraintApplierService,
        @Optional() logger?: Logger,
    ) {
        super(cdr, constraintNotifierService, logger);
        this.formGroup.statusChanges.subscribe((status) => {
            if (status === 'INVALID') {
                // provare un generico errore sulla griglia
                this.formControl?.setErrors([{ required: true }]);
            } else {
                this.formControl?.setErrors(null);
            }
        });
    }

    ngOnInit() {
        super.ngOnInit();
        this.rowSelection = this.control.rowSelection ? { ...this.control.rowSelection } : { mode: 'singleRow' };
        this.rowSelection.isRowSelectable = () => !this.isDisabled;
    }

    ngOnDestroy() {
        super.ngOnDestroy();
        this.subscriptions.forEach((sub) => sub.unsubscribe());
    }

    onReportSave() {
        const saveSub = this.actions$.pipe(ofType(cpSave)).subscribe(() => {
            this.gridApi?.expireValueCache();
        });
        this.subscriptions.push(saveSub);
    }

    onGridReady(params: GridReadyEvent) {
        this.gridApi = params.api;
        this.fitColumnIfNeed();

        this.noRowsTemplate = this.translate.instant('se-ui-datagrid_no_row_overlay');

        this.modelService
            .registerList(this.control.dataBinding.entity)
            .pipe(
                map(
                    (value) =>
                        value?.activeEntities.map(
                            (entity) => ({ ...(entity.toJSON() as JsonObject), entity, Id: entity.Id }) as JsonEntity,
                        ) ?? [],
                ),
                distinctUntilChanged((_, currentValue) => isEqual(this.model, currentValue)),
                takeUntil(this.destroyed$),
            )
            .subscribe((model) => {
                this.myModelChange(model);
            });

        // Gestione enable/disable-readonly a runtime
        this.updateFormControl();
    }

    onNotifyModelChange = (_: CellValueChangedEvent) => {
        this.edited.emit();
    };

    protected updateFormControl() {
        // Gestione enable/disable-readonly a runtime
        if (!this.gridApi) return;

        const state = this.gridApi.getColumnState();
        const columnActionButton = state.find((state) => state.pinned === 'right');
        const idColumnAction = columnActionButton?.colId;
        const allColumns = this.gridApi.getColumns();
        if (allColumns && allColumns.length > 0 && !isNil(idColumnAction)) {
            this.gridApi.setColumnsVisible([idColumnAction], !this.isDisabled);
        }
        this.control.control.config[0].readonly = this.isDisabled;
        this.fitColumnIfNeed();
    }

    getContext() {
        return {
            formGroup: this.formGroup,
            createKey: this.createKey,
        };
    }

    private createKey(rowNode: IRowNode<JsonEntity>, column: Column): string {
        const data = rowNode.data;
        return `${data?.Id}${column.getColId()}`;
    }

    onRowEditingStopped(_: RowEditingStoppedEvent) {
        this.onColumnResized();
    }

    onColumnResized(_?: ColumnResizedEvent) {
        this.gridApi?.resetRowHeights();
    }

    onGridSizeChanged(_params: GridSizeChangedEvent) {
        this.fitColumnIfNeed();
        this.changeRowHeight(window.innerWidth);
    }

    onChangeFromAdContent() {
        this.edited.emit();
    }

    onFirstDataRendered(_event: FirstDataRenderedEvent) {
        this.fitColumnIfNeed();
    }

    onRowDataUpdated(_event: RowDataUpdatedEvent) {
        this.fitColumnIfNeed();
    }

    private changeRowHeight(screenWidth: number): void {
        if (screenWidth > 1281 && screenWidth < 1440 && this.rowHeight !== 35) {
            this.rowHeight = 35;
        } else if (screenWidth > 801 && screenWidth < 1280 && this.rowHeight !== 30) {
            this.rowHeight = 30;
        } else if ((screenWidth > 1441 || screenWidth <= 801) && this.rowHeight !== 40) {
            this.rowHeight = 40;
        }
        this.gridApi?.resetRowHeights();
    }

    private fitColumnIfNeed() {
        this.gridApi?.sizeColumnsToFit();
    }

    // shorcut 'add new row'
    _onAltEnter(event: Event) {
        event.preventDefault();
        event.stopPropagation();
        this.addNewRow();
    }

    private addNewRow() {
        const newEntity = this.modelService.createModelFromPlaceholder(
            this.control.dataBinding.entity,
            this.control.dataBinding.entity.toStringSimple(),
        );
        this.edited.emit();
        this.addRow.emit(newEntity);

        setTimeout(() => {
            if (!this.gridApi) return;
            const columnState = this.gridApi.getColumnState() ?? [];
            const prevState = [...columnState];
            prevState.forEach((state) => (state.sort = null));
            this.gridApi.applyColumnState({ state: prevState, applyOrder: true });
            this.startEditing(this.control.columnsConfigs);
        }, 0);
    }

    setDataSource = (idDataSource: string, useObject: boolean = true): IDataSource | Array<string> => {
        let currentDataSource: IDataSource | Array<string> = [];

        if (idDataSource) {
            currentDataSource = this.datasourceService.getById(idDataSource) ?? [];
        }

        if (currentDataSource && useObject === false) {
            currentDataSource = this.datasourceService.getById(idDataSource)?.map((item) => item.Description) ?? [];
        }

        return currentDataSource;
    };

    protected init() {
        this.control.setModelService(this.modelService);
        this.dataBind();
        this.setCultureServiceInfo();

        this.control.removeRow = this.removeRow;
        this.control.addRow = this.onAddRow;
        this.control.removeAllRows = this.removeAllRows;
        this.control.setDataSource = this.setDataSource;
        this._enableButtonRemoveAllRows = this.control.control.config[0].deleteAllButton ?? false;

        /**passo le istanze dei servizi al controllo (modello) */
        this.control.constraintFilterService = this.constraintFilterService;
        this.control.configuratorService = this.configuratorService;
        this.control.constraintNotifier = this.constraintNotifier;
    }

    private dataBind() {
        if (!this.control.dataBinding) {
            return;
        }
        this.formControl = new FormControl('', [
            this.makeValidator(this._entitySubject, this.control.dataBinding.propertyName, false),
        ]);
    }

    private setCultureServiceInfo() {
        this.control.maskedDateFormat = this.cultureService.getShortDatePattern() || undefined;
        this.control.maskedTimeFormat = this.cultureService.getShortTimePattern() || undefined;
        this.control.maskedDateTimeFormat = this.cultureService.getShortDateTimePattern() || undefined;
        this.control.maskedDateSeparator = this.cultureService.getDateSeparator() || undefined;
        this.control.maskedTimeSeparator = this.cultureService.getTimeSeparator() || undefined;
        this.control.separatorInUse = this.cultureService.getDecimalSeparator() || ',';
    }

    protected myModelChange = (model: JsonEntity[]) => {
        if (!this.gridApi) return;
        if (!this.model) {
            this.model = model;
            this.gridApi.setGridOption('rowData', model);
        } else {
            const { addedEntities, removedEntities, updatedEntities } = this.getModelChanges(model);
            this.gridApi.applyTransaction({ update: updatedEntities });
            this.gridApi.applyTransaction({ remove: removedEntities });
            this.gridApi.applyTransaction({ add: addedEntities });
            this.model = model;
        }
    };

    protected onModelChange(_: CpEntityList | undefined) {
        return false;
    }

    private getModelChanges = (
        model: JsonEntity[],
    ): {
        addedEntities: JsonEntity[];
        removedEntities: JsonEntity[];
        updatedEntities: (Partial<JsonEntity> & { Id: string })[];
    } => {
        const addedEntities = model.filter((newItem) => !this.model?.find((oldItem) => oldItem.Id === newItem.Id));
        const removedEntities =
            this.model?.filter((oldItem) => !model.find((newItem) => newItem.Id === oldItem.Id)) ?? [];
        const updatedEntities = model?.map((je) => this.getDataDiff(je)).filter(isNotEmpty) ?? ([] as any);
        return { addedEntities, removedEntities, updatedEntities };
    };

    private getDataDiff = (jsonEntity: JsonEntity): (Partial<JsonEntity> & { Id: string }) | null => {
        const oldJsonEntity = this.model?.find((row) => row.Id === jsonEntity.Id);
        if (!oldJsonEntity || isEqual(oldJsonEntity, jsonEntity)) return null;
        return jsonEntity;
    };

    onAddRow = () => {
        this.addNewRow();
    };

    removeRow = (cellRendererParams: ICellRendererParams, _: string) => {
        if (!this.gridApi) return;
        this.gridApi.stopEditing(true);
        this.modalDialogNotificationService
            .notify({
                message: this.translate.instant('DeleteSingleRow'),
                title: this.translate.instant('Warning'),
                type: NotificationType.information,
                buttonYes: true,
                buttonNo: true,
                buttonOk: false,
                buttonCancel: false,
            })
            .pipe(first())
            .subscribe((button) => {
                if (button === ButtonClick.yes) {
                    this.modelService.removeEntity(cellRendererParams.data.entity);

                    // Determino se mostrare o meno il no data overlay
                    this.edited.emit();
                    this.deleteRow.emit();
                }
            });
    };

    removeAllRows = () => {
        if (!this.gridApi) return;
        this.gridApi?.stopEditing(true);
        this.modalDialogNotificationService
            .notify({
                message: this.translate.instant('DeleteAllRows'),
                title: this.translate.instant('Warning'),
                type: NotificationType.information,
                buttonYes: true,
                buttonNo: true,
                buttonOk: false,
                buttonCancel: false,
            })
            .pipe(first())
            .subscribe((button) => {
                if (button === ButtonClick.yes) {
                    this.model?.forEach((jsonEntity: JsonEntity) => {
                        if (jsonEntity.entity) this.modelService.removeEntity(jsonEntity.entity);
                    });
                    this.edited.emit();
                }
            });
    };

    refreshGrid() {
        this.gridApi?.refreshCells({ force: true });
    }

    protected startEditing(columnConfigArray: Array<IConfigColumnGridControl>): void {
        if (!this.gridApi) return;
        let firstRequiredColumn = this.getFirstRequiredColumn(columnConfigArray);
        if (firstRequiredColumn) {
            const startCellInEdit: StartEditingCellParams = {
                rowIndex: (this.model?.length ?? 0) - 1,
                colKey: firstRequiredColumn,
            };
            this.gridApi.startEditingCell(startCellInEdit);
        } else {
            if (this.isColumnVisible(columnConfigArray[0].visibilityStatus)) {
                const startCellInEdit: StartEditingCellParams = {
                    rowIndex: (this.model?.length ?? 0) - 1,
                    colKey: columnConfigArray[0].dataBinding,
                };
                this.gridApi.startEditingCell(startCellInEdit);
            }
        }
    }

    protected getFirstRequiredColumn(columnConfigArray: any): any {
        const firstRequiredColumn = columnConfigArray.find((elem: IConfigColumnGridControl) => elem.required === true);
        return firstRequiredColumn?.dataBinding || null;
    }

    protected isColumnVisible(visibilityStatus: IVisibilityStatus) {
        return visibilityStatus === IVisibilityStatus.VISIBLE;
    }

    getRowId: GetRowIdFunc = (params) => params.data.Id;
}
